import React, { useId } from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface CampusFacilityMultiSelectProps {
  facilities: Array<{ id: string; name: string }>;
  /** Selected facility ids; never empty — deselecting the last one is ignored. */
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  sx?: SxProps<Theme>;
}

/** Which campus facilities a roll-up view shows; hidden for a single-facility campus. */
const CampusFacilityMultiSelect: React.FC<CampusFacilityMultiSelectProps> = ({
  facilities,
  value,
  onChange,
  label = 'Facilities',
  sx = { minWidth: 220 }
}) => {
  const labelId = `${useId()}-label`;
  if (facilities.length <= 1) return null;

  const allSelected = value.length === facilities.length;
  const nameOf = (id: string) => facilities.find((f) => f.id === id)?.name || id;

  return (
    <FormControl size="small" sx={sx}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple
        value={value}
        label={label}
        onChange={(event) => {
          const next = event.target.value;
          const ids = typeof next === 'string' ? next.split(',') : next;
          if (ids.length > 0) onChange(ids);
        }}
        renderValue={(selected) =>
          allSelected
            ? 'All campus facilities'
            : (selected as string[]).map(nameOf).join(', ')
        }
        SelectDisplayProps={{ 'aria-label': label }}
      >
        {facilities.map((facility) => (
          <MenuItem key={facility.id} value={facility.id}>
            <Checkbox size="small" checked={value.includes(facility.id)} />
            <ListItemText primary={facility.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CampusFacilityMultiSelect;
